/* Возобновляемая загрузка частями с серверным подтверждением каждого смещения. */

import type { Attachment, UploadState } from '@andresot/protocol';
import {
	ApiError,
	authorizedUpload,
	request,
	getAuthorizationGeneration,
	assertAuthorizationGeneration
} from './client.js';

export const MAX_FILE_SIZE = 4 * 1024 ** 3;
const RETRIES = 4;

export interface UploadProgress {
	loaded: number;
	total: number;
	ratio: number;
}

export interface Upload {
	promise: Promise<Attachment>;
	cancel: () => void;
}

type StoredResume = {
	id: string;
	name: string;
	size: number;
	lastModified: number;
	expiresAt: number;
};

function resumeKey(file: File): string {
	return `andresot-upload:${file.name}:${file.size}:${file.lastModified}`;
}

function readResume(file: File): StoredResume | null {
	try {
		const value = JSON.parse(
			localStorage.getItem(resumeKey(file)) || 'null'
		) as StoredResume | null;
		return value && value.expiresAt > Date.now() ? value : null;
	} catch {
		return null;
	}
}

function saveResume(file: File, upload: UploadState): void {
	localStorage.setItem(
		resumeKey(file),
		JSON.stringify({
			id: upload.id,
			name: file.name,
			size: file.size,
			lastModified: file.lastModified,
			expiresAt: upload.expiresAt
		} satisfies StoredResume)
	);
}

function clearResume(file: File): void {
	localStorage.removeItem(resumeKey(file));
}

async function responseError(response: Response, fallback: string): Promise<ApiError> {
	let body: { error?: string } = {};
	try {
		body = (await response.json()) as typeof body;
	} catch {
		// Ошибка прокси может быть не JSON — показываем нейтральный текст.
	}
	return new ApiError(body.error ?? fallback, response.status);
}

function wait(ms: number, signal: AbortSignal): Promise<void> {
	return new Promise((resolve, reject) => {
		if (signal.aborted) {
			reject(new DOMException('Загрузка отменена', 'AbortError'));
			return;
		}
		const onAbort = () => {
			clearTimeout(timer);
			reject(new DOMException('Загрузка отменена', 'AbortError'));
		};
		const timer = setTimeout(() => {
			signal.removeEventListener('abort', onAbort);
			resolve();
		}, ms);
		signal.addEventListener('abort', onAbort, { once: true });
	});
}

export function uploadFile(file: File, onProgress?: (progress: UploadProgress) => void): Upload {
	const generation = getAuthorizationGeneration();
	const controller = new AbortController();
	let activeId: string | null = null;

	const promise = (async (): Promise<Attachment> => {
		if (file.size > MAX_FILE_SIZE) throw new ApiError('Файл больше 4 ГБ', 413);
		try {
			let upload: UploadState | null = null;
			const saved = readResume(file);
			if (saved) {
				try {
					upload = (
						await request<{ upload: UploadState }>(`/api/uploads/${saved.id}`, { generation })
					).upload;
				} catch {
					clearResume(file);
				}
			}
			if (!upload) {
				upload = (
					await request<{ upload: UploadState }>('/api/uploads', {
						generation,
						method: 'POST',
						body: {
							name: file.name || 'file',
							size: file.size,
							mimeType: file.type || 'application/octet-stream'
						}
					})
				).upload;
			}
			if (!upload) throw new ApiError('Не удалось создать загрузку', 0);
			let currentUpload: UploadState = upload;
			activeId = currentUpload.id;
			saveResume(file, currentUpload);
			let offset = currentUpload.offset;
			while (offset < file.size) {
				assertAuthorizationGeneration(generation);
				if (controller.signal.aborted) throw new DOMException('Загрузка отменена', 'AbortError');
				const end = Math.min(file.size, offset + currentUpload.chunkSize);
				const chunk = file.slice(offset, end);
				let response: Response | null = null;
				for (let attempt = 0; attempt <= RETRIES; attempt += 1) {
					try {
						assertAuthorizationGeneration(generation);
						const chunkStart = offset;
						response = await authorizedUpload(`/api/uploads/${currentUpload.id}`, {
							method: 'PATCH',
							headers: {
								'content-type': 'application/offset+octet-stream',
								'upload-offset': String(offset)
							},
							body: chunk,
							signal: controller.signal,
							// Доля растёт по мере отправки байт, а не рывком по концу
							// куска: кусок бывает в несколько мегабайт, и ожидание
							// на нуле выглядело зависшей отправкой.
							onProgress: (sent) => {
								const loaded = Math.min(file.size, chunkStart + sent);
								onProgress?.({ loaded, total: file.size, ratio: loaded / file.size });
							}
						});
						if (response.status === 409 || response.status === 423) {
							const retryStatus = response.status;
							const snapshot: { upload: UploadState } = await request(
								`/api/uploads/${currentUpload.id}`,
								{ generation }
							);
							currentUpload = snapshot.upload;
							offset = currentUpload.offset;
							response = null;
							if (retryStatus === 423) await wait(500, controller.signal);
							break;
						}
						if (!response.ok)
							throw await responseError(response, 'Не удалось передать часть файла');
						break;
					} catch (error) {
						assertAuthorizationGeneration(generation);
						if (controller.signal.aborted) throw error;
						if (error instanceof ApiError && error.status >= 400 && error.status < 500) throw error;
						if (attempt === RETRIES) throw error;
						await wait(400 * 2 ** attempt, controller.signal);
					}
				}
				if (!response) continue;
				offset = Number(response.headers.get('upload-offset') || end);
				currentUpload = { ...currentUpload, offset };
				saveResume(file, currentUpload);
				onProgress?.({ loaded: offset, total: file.size, ratio: offset / file.size });
			}

			const completed = await request<{ attachment: Attachment }>(
				`/api/uploads/${currentUpload.id}/complete`,
				{ method: 'POST', body: {}, generation }
			);
			clearResume(file);
			return completed.attachment;
		} catch (error) {
			if (
				controller.signal.aborted ||
				(error instanceof DOMException && error.name === 'AbortError')
			) {
				clearResume(file);
				throw new ApiError('Загрузка отменена', 0);
			}
			throw error;
		}
	})();

	return {
		promise,
		cancel: () => {
			controller.abort();
			if (activeId && generation === getAuthorizationGeneration())
				void request(`/api/uploads/${activeId}`, { method: 'DELETE', generation }).catch(
					() => undefined
				);
		}
	};
}

export function guessKind(file: File): Attachment['kind'] {
	if (file.type.startsWith('image/')) return 'image';
	if (file.type.startsWith('video/')) return 'video';
	if (file.type.startsWith('audio/')) return 'voice';
	return 'file';
}
